/**
 * FAQ page interactions:
 * - GSAP reveal matching the Club page lifecycle. The whole section (l-faq-page__inner) is the
 *   single [data-l-faq-reveal] container — one timeline, like club's one-reveal-per-section — so
 *   nothing is animated twice. It stays invisible until revealed via the l-faq-reveal-prep guard.
 * - Desktop Quick Find rail: scrollspy + smooth scroll (never opens/closes FAQ items).
 */
;(function () {
	window.__lFaqRevealActive = true

	var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
	var revealObserver = null

	function initFaqReveal() {
		var els = document.querySelectorAll('[data-l-faq-reveal]:not(.is-revealed)')

		if (!els.length) return

		if (prefersReducedMotion || !window.gsap || !window.IntersectionObserver) {
			revealFaqStatic()

			return
		}

		if (revealObserver) revealObserver.disconnect()

		revealObserver = new IntersectionObserver(handleRevealEntries, {
			root: null,
			rootMargin: '0px 0px -16% 0px',
			threshold: 0.01,
		})

		els.forEach(function (el) {
			el.classList.add('is-reveal-bound')
			revealObserver.observe(el)
		})
	}

	function handleRevealEntries(entries) {
		entries.forEach(function (entry) {
			if (!entry.isIntersecting) return

			revealObserver.unobserve(entry.target)
			try {
				playFaqReveal(entry.target)
			} catch (e) {
				markRevealComplete(entry.target)
			}
		})
	}

	function playFaqReveal(el) {
		if (el.classList.contains('is-revealed') || el.classList.contains('is-revealing')) return

		var parts = getRevealParts(el)
		var media = getRevealMedia(el)
		var animatedTargets = [el].concat(parts, media)
		var tl = window.gsap.timeline({
			defaults: {
				ease: 'power3.out',
				overwrite: 'auto',
			},
			onComplete: function () {
				markRevealComplete(el)
				window.gsap.set(animatedTargets, {
					clearProps: 'opacity,visibility,transform,willChange',
				})
			},
		})

		el.classList.add('is-revealing')
		window.gsap.set(animatedTargets, { willChange: 'transform,opacity' })

		tl.fromTo(el, { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.65, ease: 'power2.out' }, 0)

		if (parts.length) {
			tl.fromTo(
				parts,
				{ autoAlpha: 0, y: 18, scale: 0.99 },
				{
					autoAlpha: 1,
					y: 0,
					scale: 1,
					duration: 0.75,
					stagger: { each: 0.055, from: 'start' },
				},
				0.08
			)
		}

		if (media.length) {
			tl.fromTo(media, { scale: 1.025 }, { scale: 1, duration: 1, ease: 'power2.out', transformOrigin: '50% 55%' }, 0)
		}
	}

	function getRevealParts(el) {
		// One reveal container (the section), so this collects every animated part exactly once.
		return getVisibleNodes(
			el.querySelectorAll(
				'.l-faq-page__hero-title, .l-faq-page__quick-find, .l-faq-page__group-heading, .l-faq-page__item, .l-faq-page__support-card'
			)
		)
	}

	function getRevealMedia(el) {
		return getVisibleNodes(el.querySelectorAll('.l-faq-page__hero-img'))
	}

	function getVisibleNodes(nodes) {
		var seen = []

		return Array.prototype.filter.call(nodes, function (node) {
			if (!node || seen.indexOf(node) !== -1 || !node.getClientRects().length) return false
			seen.push(node)

			return true
		})
	}

	function markRevealComplete(el) {
		el.classList.remove('is-reveal-bound')
		el.classList.remove('is-revealing')
		el.classList.add('is-revealed')

		if (!document.querySelector('[data-l-faq-reveal]:not(.is-revealed)')) {
			document.documentElement.classList.remove('l-faq-reveal-prep')
		}
	}

	function revealFaqStatic() {
		document.documentElement.classList.remove('l-faq-reveal-prep')

		document.querySelectorAll('[data-l-faq-reveal]').forEach(function (el) {
			markRevealComplete(el)
		})
	}

	try {
		initFaqReveal()
	} catch (e) {
		revealFaqStatic()
	}
})()

if (!customElements.get('l-faq-page')) {
	/**
	 * Enhances the server-rendered Quick Find rail:
	 * - Scrollspy: highlights the group currently under the header line.
	 * - Smooth, header-aware scroll on click (reduced-motion respected).
	 * It deliberately never opens/closes FAQ items — each item toggles on its own and
	 * multiple may stay open (see data-one-open="false" on a-faq-item).
	 */
	class LTAFaqPage extends HTMLElement {
		constructor() {
			super()
			this._onNavClick = this._onNavClick.bind(this)
			this._onScroll = this._onScroll.bind(this)
			this._scrollRaf = 0
			this._spyPaused = false
			this._spyResumeTimer = 0
			this._activeId = null
		}

		connectedCallback() {
			this.quickFindList = this.querySelector('[data-faq-quick-find-list]')
			this.groups = Array.prototype.slice.call(this.querySelectorAll('[data-faq-group]'))
			if (!this.quickFindList || !this.groups.length) return

			this._renderNav()
			this.navLinks = Array.prototype.slice.call(this.quickFindList.querySelectorAll('[data-faq-nav-link]'))
			if (!this.navLinks.length) return

			this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches

			this.quickFindList.addEventListener('click', this._onNavClick)
			window.addEventListener('scroll', this._onScroll, { passive: true })
			window.addEventListener('resize', this._onScroll, { passive: true })

			this._updateActiveFromScroll()
		}

		/* Build the rail from the rendered FAQ groups. These are theme blocks, so they can't be
		   enumerated in Liquid via section.blocks — the DOM is the source of truth. Each link is a
		   real anchor (href="#group-id") so it degrades to a native jump if the listeners fail. */
		_renderNav() {
			const fragment = document.createDocumentFragment()

			this.groups.forEach((group, index) => {
				if (!group.id) return

				const heading = group.querySelector('.l-faq-page__group-heading')
				const label = (group.dataset.faqGroupTitle || (heading && heading.textContent) || '').trim()
				if (!label) return

				const link = document.createElement('a')
				link.className = 'l-faq-page__quick-button l-button-reset'
				link.href = '#' + group.id
				link.dataset.faqNavLink = group.id
				link.textContent = label

				if (index === 0) {
					link.classList.add('is-active')
					link.setAttribute('aria-current', 'true')
				}

				fragment.appendChild(link)
			})

			this.quickFindList.textContent = ''
			this.quickFindList.appendChild(fragment)
		}

		disconnectedCallback() {
			if (this.quickFindList) this.quickFindList.removeEventListener('click', this._onNavClick)
			window.removeEventListener('scroll', this._onScroll)
			window.removeEventListener('resize', this._onScroll)
			if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf)
			if (this._spyResumeTimer) clearTimeout(this._spyResumeTimer)
		}

		/* Combined fixed header + announcement bar height — drives both the scroll target
		   offset and the scrollspy activation line. Falls back to the CSS defaults. */
		_topOffset() {
			const root = getComputedStyle(document.documentElement)
			const header = parseFloat(root.getPropertyValue('--l-header-height')) || 74
			const announcement = parseFloat(root.getPropertyValue('--l-announcement-bar-height')) || 0

			return header + announcement
		}

		_onScroll() {
			if (this._spyPaused || this._scrollRaf) return

			this._scrollRaf = requestAnimationFrame(() => {
				this._scrollRaf = 0
				this._updateActiveFromScroll()
			})
		}

		/* Active group = the last one whose top edge has crossed above the activation line
		   (a horizontal line just below the sticky header). Canonical table-of-contents spy.
		   The line sits comfortably below the click-scroll landing (scroll-margin-top), so a
		   group clicked in the rail stays unambiguously active after the smooth scroll settles. */
		_updateActiveFromScroll() {
			const line = this._topOffset() + 60
			let activeId = this.groups[0].id

			for (let i = 0; i < this.groups.length; i++) {
				const group = this.groups[i]
				if (!group.id) continue

				if (group.getBoundingClientRect().top - line <= 0) {
					activeId = group.id
				} else {
					break
				}
			}

			this._setActiveLink(activeId)
		}

		_setActiveLink(groupId) {
			if (!groupId || groupId === this._activeId) return
			this._activeId = groupId

			this.navLinks.forEach((link) => {
				const isActive = link.dataset.faqNavLink === groupId
				link.classList.toggle('is-active', isActive)

				if (isActive) link.setAttribute('aria-current', 'true')
				else link.removeAttribute('aria-current')
			})
		}

		_onNavClick(event) {
			const link = event.target.closest('[data-faq-nav-link]')
			if (!link || !this.quickFindList.contains(link)) return

			const groupId = link.dataset.faqNavLink
			const group = this.groups.find((item) => item.id === groupId)
			if (!group) return

			event.preventDefault()

			// Reflect the choice now and stop the spy from fighting the animated scroll.
			this._setActiveLink(groupId)
			this._pauseSpy()

			const top = Math.max(0, group.getBoundingClientRect().top + window.pageYOffset - this._topOffset() - 24)
			window.scrollTo({ top: top, behavior: this._reduceMotion ? 'auto' : 'smooth' })

			// Keep the URL shareable without re-triggering the browser's own jump.
			if (window.history && window.history.replaceState) {
				window.history.replaceState(null, '', '#' + groupId)
			}
		}

		_pauseSpy() {
			this._spyPaused = true
			if (this._spyResumeTimer) clearTimeout(this._spyResumeTimer)

			this._spyResumeTimer = setTimeout(
				() => {
					this._spyPaused = false
					this._updateActiveFromScroll()
				},
				this._reduceMotion ? 100 : 700
			)
		}
	}

	customElements.define('l-faq-page', LTAFaqPage)
}
